import { useQuestionsTabTranslations } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/components/i18n";
import { useGenerateEditAnswer } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/data";
import { AiSuggestion } from "@app/shared/components/ai/AiSuggestion";
import { Icon, type IconName } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Textfield } from "@design-system/Inputs/Textfield";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import {
    AnswerStatus,
    type GetQuestionsQuery,
} from "@generated/client/graphql";
import { useMemo, useState, type ChangeEvent, type FC } from "react";

import { useUpsertAiSuggestion } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/data";
import { questionnaireLocaleAtom } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/ctx";
import { SupportedLocales } from "@app/shared/utils/locales";
import { Box } from "@design-system/Layout/Box";
import { cn } from "@design-system/Utilities";
import { useAtomValue } from "jotai";
import { AnswerAiSuggestedResources } from "./SuggestedResources";
import { AnswerAiSuggestionBlock } from "./SuggestionBlock";
import { EditAction } from "./types";

const languages = (): { id: string; label: string }[] => {
    const { t, keys } = useQuestionsTabTranslations();
    return [
        {
            id: "en",
            label: t(keys.english),
        },
        {
            id: "fr",
            label: t(keys.french),
        },
        {
            id: "de",
            label: t(keys.german),
        },
        {
            id: "es",
            label: t(keys.spanish),
        },
        {
            id: "it",
            label: t(keys.italian),
        },
        {
            id: "pt",
            label: t(keys.portuguese),
        },
        {
            id: "ch",
            label: t(keys.chinese),
        },
        {
            id: "jp",
            label: t(keys.japanese),
        },
    ];
};

export const EditSuggestionBlock: FC<{
    question: GetQuestionsQuery["questions"][number];
}> = ({ question }) => {
    const { t, keys } = useQuestionsTabTranslations();

    const options = [
        {
            id: EditAction.TRANSLATE,
            iconName: "translate",
            label: t(keys.translate_answer_to),
        },
        {
            id: EditAction.CONCISE,
            label: t(keys.make_it_more_concise),
            iconName: "shorten",
        },
        {
            id: EditAction.DETAILED,
            label: t(keys.make_it_more_detailed),
            iconName: "larger",
        },
        {
            id: EditAction.CUSTOM,
            label: t(keys.type_your_own_prompt),
            iconName: "circleInfo",
        },
    ];

    const [isOpen, setIsOpen] = useState(false);

    const [action, setAction] = useState<EditAction | undefined>(undefined);
    const [instructions, setInstructions] = useState<string | undefined>(
        undefined,
    );
    const languageOptions = languages();

    const [language, setLanguage] = useState<string>("");

    const { generateEditAnswer, generateEditAnswerData, isPending } =
        useGenerateEditAnswer(
            question.questionnaireId ?? "",
            question.id,
            action ?? EditAction.CUSTOM,
            language,
        );

    const { upsertAiSuggestion } = useUpsertAiSuggestion(
        question.id,
        generateEditAnswerData ?? {},
    );

    const handleSaveAiSuggestion = () => {
        if (generateEditAnswerData) {
            upsertAiSuggestion(generateEditAnswerData);
            setIsOpen(false);
            setAction(undefined);
            setInstructions(undefined);
        }
    };
    const handleCancel = (open: boolean) => () => {
        setIsOpen(open);
        setInstructions(undefined);
        setAction(undefined);
        setLanguage("");
    };

    const handleActionClick =
        (action: EditAction, language?: string, instructions?: string) =>
        () => {
            setAction(action);
            setLanguage(language ?? "");
            setInstructions(instructions ?? "");
            generateEditAnswer({
                questionId: question.id,
                action: action,
                language: language,
                instructions: instructions ?? "",
            });

            setInstructions(undefined);
        };

    const handleLanguageChange = (value: string[]) => {
        if (value.length > 0) {
            const language = languageOptions.find(
                (option) => option.id === value[0],
            )?.label;
            setLanguage(language ?? "");
        }
        if (value.length === 0) {
            const language = languageOptions.find(
                (option) => option.id === value[0],
            )?.label;
            setLanguage(language ?? "");
        }
    };

    const handleSetInstructions = (e: ChangeEvent<HTMLInputElement>) => {
        setInstructions(e.target.value);
        setAction(EditAction.CUSTOM);
    };

    const questionWithEditAnswer = useMemo(() => {
        if (!generateEditAnswerData) return question;

        const newQuestion = {
            ...question,
            answer: {
                id: question.answer?.id ?? "",
                value: question.answer?.value ?? "",
                comment: question.answer?.comment ?? "",
                status: question.answer?.status ?? AnswerStatus.Validated,
                lastGeneratedAt: question.answer?.lastGeneratedAt ?? "",
                evidenceFiles: question.answer?.evidenceFiles ?? [],
                aiSuggestion: generateEditAnswerData,
            },
        };

        return newQuestion;
    }, [question, generateEditAnswerData]);

    const handleEditActionChange = (value: string[]) => {
        const action = options.find((option) => option.label === value[0])?.id;
        setAction(action as EditAction);
        setLanguage("");
    };

    return (
        <Modal open={isOpen} onOpenChange={handleCancel(!isOpen)}>
            <Modal.Trigger asChild>
                <FlexRow
                    alignItems="center"
                    gap="2"
                    className="cursor-pointer w-full"
                >
                    <Icon name="edit" size="sm" className="text-accent-1-400" />
                    {t(keys.ai_edit_answer_button)}
                </FlexRow>
            </Modal.Trigger>
            <Modal.Overlay centerContent className="py-5" scrollable>
                <Modal.Content className="w-fit max-w-[880px] min-w-[500px]">
                    <Modal.Header>
                        <Modal.Title>
                            <FlexRow gap="2" alignItems="center">
                                <Icon
                                    name="stars"
                                    size="sm"
                                    className="text-accent-1-400"
                                />
                                <Text
                                    variant="body-bold"
                                    className="text-accent-1-400"
                                >
                                    {t(keys.instructions_modal_title)}
                                </Text>
                            </FlexRow>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="2">
                            <Text variant="body">
                                {t(keys.instructions_modal_subtitle)}
                            </Text>
                            <AiAnswerBlock question={question} />
                            <Text variant="body" className="text-secondary">
                                {t(keys.instructions_ai_actions)}
                            </Text>
                            <AiEditAnswerQuickActionBlock
                                options={options}
                                language={language}
                                editAction={action}
                                instructions={instructions ?? ""}
                                onClick={handleActionClick}
                                handleLanguageChange={handleLanguageChange}
                                handleEditActionChange={handleEditActionChange}
                                handleSetInstructions={handleSetInstructions}
                                isPending={isPending}
                            />
                        </FlexCol>
                        {generateEditAnswerData && (
                            <FlexCol gap="2">
                                {!!generateEditAnswerData && (
                                    <>
                                        <Text variant="body-bold">
                                            {t(
                                                keys.instructions_modal_new_answer,
                                            )}
                                        </Text>
                                        <AiAnswerBlock
                                            question={questionWithEditAnswer}
                                        />
                                        <FlexRow gap="2" justifyContent="end">
                                            <Button
                                                variant="outlined"
                                                onClick={handleCancel(false)}
                                            >
                                                {t(
                                                    keys.instructions_modal_cancel,
                                                )}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={handleSaveAiSuggestion}
                                            >
                                                {t(
                                                    keys.instructions_modal_save,
                                                )}
                                            </Button>
                                        </FlexRow>
                                    </>
                                )}
                            </FlexCol>
                        )}
                    </Modal.Body>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

const AiEditAnswerQuickActionBlock: FC<{
    options: {
        id: EditAction;
        label: string;
        iconName: string;
    }[];
    editAction: EditAction | undefined;
    language: string;
    instructions: string;
    onClick: (
        action: EditAction,
        language?: string,
        instructions?: string,
    ) => () => void;
    handleLanguageChange: (value: string[]) => void;
    handleEditActionChange: (value: string[]) => void;
    handleSetInstructions: (e: ChangeEvent<HTMLInputElement>) => void;
    isPending: boolean;
}> = ({
    options,
    editAction,
    language,
    instructions,
    onClick,
    handleLanguageChange,
    handleEditActionChange,
    handleSetInstructions,
    isPending,
}) => {
    const { t, keys } = useQuestionsTabTranslations();

    const isDisabled = useMemo(() => {
        if (editAction === EditAction.CUSTOM) {
            return !instructions?.trim();
        }
        if (editAction === EditAction.TRANSLATE) {
            return !language;
        }
        return !editAction;
    }, [instructions, editAction, language]);

    return (
        <>
            <FlexRow gap="2" alignItems="center">
                <Dropdown
                    selectType="single"
                    value={editAction ? [editAction] : []}
                    onValueChange={handleEditActionChange}
                    closeOnValueChange={false}
                >
                    <Dropdown.ChipFilterTrigger
                        label={t(keys.edit_action_dropdown_trigger)}
                        options={options}
                        valueIdentifierKey="id"
                        valueDisplayKey="label"
                    />
                    <Dropdown.Content align="start" hasPortal>
                        {options.map((option) => (
                            <Dropdown.SelectableItem
                                value={option.label}
                                key={option.id}
                            >
                                <Dropdown.ItemIcon
                                    name={option.iconName as IconName}
                                    className="!text-primary"
                                />
                                <Dropdown.ItemText>
                                    {t(option.label)}
                                </Dropdown.ItemText>
                            </Dropdown.SelectableItem>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                {editAction === EditAction.TRANSLATE && (
                    <LanguageDropdown
                        value={language}
                        onChange={handleLanguageChange}
                    />
                )}
            </FlexRow>
            {editAction === EditAction.CUSTOM && (
                <Textfield
                    value={instructions}
                    onChange={handleSetInstructions}
                    placeholder={t(keys.instructions_modal_placeholder)}
                    className="w-full"
                />
            )}
            <FlexRow gap="2" justifyContent="end">
                <Button
                    variant="flatAccent"
                    size="md"
                    onClick={onClick(
                        editAction ?? EditAction.CUSTOM,
                        language,
                        instructions,
                    )}
                    disabled={isDisabled}
                    loading={isPending}
                >
                    <Button.Icon name="stars" size="sm" />
                    {t(keys.instructions_modal_send_suggestion)}
                </Button>
            </FlexRow>
        </>
    );
};

const AiAnswerBlock: FC<{
    question: GetQuestionsQuery["questions"][number];
}> = ({ question }) => {
    const { t, keys } = useQuestionsTabTranslations();
    const currentLocale = useAtomValue(questionnaireLocaleAtom);
    return (
        <Box className="bg-tertiary rounded-lg p-1 max-h-[200px] overflow-y-auto">
            <AiSuggestion.Body
                className={cn(!question?.answer?.aiSuggestion && "pb-5")}
            >
                <AnswerAiSuggestionBlock
                    question={question}
                    locale={currentLocale ?? SupportedLocales.EN}
                    withInsert={false}
                    withCopy={false}
                />
                <AnswerAiSuggestedResources
                    question={question}
                    withButton={false}
                />
                {!!question?.answer?.aiSuggestion?.motivation && (
                    <FlexCol gap="4">
                        <AiSuggestion.Separator />
                        <AiSuggestion.Block
                            withCopy={false}
                            withInsert={false}
                            title={t(keys.ai_suggestion_motivation)}
                            content={
                                question?.answer?.aiSuggestion?.motivation ?? ""
                            }
                        />
                    </FlexCol>
                )}
            </AiSuggestion.Body>
        </Box>
    );
};

const LanguageDropdown: FC<{
    value: string;
    onChange: (value: string[]) => void;
}> = ({ value, onChange }) => {
    const { t, keys } = useQuestionsTabTranslations();
    const languageOptions = languages();

    return (
        <Dropdown value={value ? [value] : []} onValueChange={onChange}>
            <Dropdown.Trigger>
                <Button variant="outlined" size="sm">
                    {value
                        ? languageOptions.find(
                              (option) => option.label === value,
                          )?.label
                        : t(keys.language_dropdown_trigger)}
                    <Button.Icon name="chevronDown" size="sm" />
                </Button>
            </Dropdown.Trigger>
            <Dropdown.Content hasPortal>
                {languageOptions.map((option) => (
                    <Dropdown.SelectableItem value={option.id} key={option.id}>
                        {option.label}
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};
